import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import './App.css';

// Import Contextu pro košík
import { CartProvider } from './context/CartContext';

// Komponenty
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Features from './components/Features';
import Banner from './components/Banner';
import Gallery from './components/Gallery';
import FAQ from './components/FAQ';
import Footer from './components/Footer';

// Stránky
import GalleryPage from './components/GalleryPage';
import ContactPage from './components/ContactPage';
import EshopPage from './components/EshopPage';
import AboutMePage from './components/AboutMePage';
import CheckoutPage from './components/CheckoutPage';
import ProfilePage from './components/ProfilePage';
import LoginPage from './components/LoginPage';
import LegalPage from './components/LegalPage';

// --- PRÁVNÍ TEXTY ---
const obchodniPodminkyText = `Tyto obchodní podmínky platí pro nákup v internetovém obchodě Václav Kolařík – Umělecké řezbářství.

1. Objednávka: Kupní smlouva vzniká odesláním objednávky prostřednictvím e-shopu. Vzhledem k povaze zboží (ruční práce a přírodní materiál) se mohou drobné detaily, jako je kresba dřeva nebo odstín, mírně lišit od fotografií.
2. Platba: Přijímáme platby bankovním převodem (včetně QR kódů) nebo hotově při osobním převzetí.
3. Doprava: Zboží zasíláme prostřednictvím Zásilkovny na výdejní místa nebo na adresu. Možný je také osobní odběr v dílně (Svatobořice).
4. Odstoupení od smlouvy: Kupující má právo odstoupit od smlouvy do 14 dnů od převzetí zboží. Pozor: Toto právo se nevztahuje na zboží upravené nebo vyrobené přímo na zakázku zákazníka (zakázkové sochy apod.).`;

const gdprText = `Správcem osobních údajů je Václav Kolařík, se sídlem Příční 382/5, Svatobořice 696 04.

1. Účel zpracování: Vaše osobní údaje (jméno, e-mail, telefon, adresa) zpracováváme výhradně za účelem vyřízení vaší objednávky, komunikace ohledně zakázky a plnění zákonných účetních povinností.
2. Doba uchování: Údaje uchováváme po dobu nezbytnou k vyřízení objednávky a následně dle zákonných lhůt pro archivaci účetních dokladů.
3. Vaše práva: Máte právo na přístup ke svým údajům, jejich opravu, výmaz nebo omezení zpracování. Vaše data jsou u nás v bezpečí a nikdy je neprodáváme třetím stranám.`;

const cookiesText = `Tento web používá pouze nezbytné technické soubory cookies.

Tyto soubory jsou nezbytné pro správné fungování webových stránek. Umožňují například:
- Ukládání produktů do nákupního košíku.
- Zabezpečené přihlášení k vašemu uživatelskému profilu.
- Správné zobrazení animací a grafických prvků.

Nepoužíváme žádné marketingové ani sledovací cookies třetích stran pro cílení reklamy. Prohlížením tohoto webu vyjadřujete souhlas s používáním těchto nezbytných prvků.`;


// Pomocná komponenta pro animovaný obal stránky
const PageWrapper = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.5 }}
  >
    {children}
  </motion.div>
);

// Sdružená komponenta pro domovskou stránku
const Home = () => (
  <PageWrapper>
    <Hero />
    <About />
    <Features />
    <Banner />
    <Gallery />
    <FAQ />
  </PageWrapper>
);

// KOMPONENTA COOKIE LIŠTY
const CookieConsent = () => {
  const [isVisible, setIsVisible] = React.useState(!localStorage.getItem('cookieConsent'));

  const acceptCookies = () => {
    localStorage.setItem('cookieConsent', 'true');
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '20px',
            right: '20px',
            maxWidth: '400px',
            backgroundColor: '#2b1f18',
            color: '#fff',
            padding: '20px',
            borderRadius: '15px',
            zIndex: 10000,
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            border: '1px solid #d1b892',
            marginLeft: 'auto',
          }}
        >
          <h4 style={{ color: '#d1b892', marginBottom: '10px', marginTop: 0 }}>🍪 Cookies na dílně</h4>
          <p style={{ fontSize: '0.85rem', lineHeight: '1.5', marginBottom: '15px' }}>
            Používám jen nezbytné cookies, aby web a košík fungovaly, jak mají. Prohlížením webu s tím souhlasíte.
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={acceptCookies}
              style={{
                backgroundColor: '#d1b892',
                color: '#2b1f18',
                border: 'none',
                padding: '8px 20px',
                borderRadius: '8px',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}
            >
              Rozumím
            </button>
            <Link 
              to="/cookies" 
              style={{ color: '#aaa', fontSize: '0.8rem', alignSelf: 'center', textDecoration: 'none' }}
            >
              Více info
            </Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Komponenta pro animované směrování
const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Home />} />
        <Route path="/o-mne" element={<PageWrapper><AboutMePage /></PageWrapper>} />
        <Route path="/galerie" element={<PageWrapper><GalleryPage /></PageWrapper>} />
        <Route path="/kontakt" element={<PageWrapper><ContactPage /></PageWrapper>} />
        <Route path="/eshop" element={<PageWrapper><EshopPage /></PageWrapper>} />
        <Route path="/objednavka" element={<PageWrapper><CheckoutPage /></PageWrapper>} />
        <Route path="/login" element={<PageWrapper><LoginPage /></PageWrapper>} />
        <Route path="/profil" element={<PageWrapper><ProfilePage /></PageWrapper>} />
        
        <Route path="/obchodni-podminky" element={<PageWrapper><LegalPage title="Obchodní podmínky" content={obchodniPodminkyText} /></PageWrapper>} />
        <Route path="/gdpr" element={<PageWrapper><LegalPage title="Ochrana osobních údajů (GDPR)" content={gdprText} /></PageWrapper>} />
        <Route path="/cookies" element={<PageWrapper><LegalPage title="Zásady používání cookies" content={cookiesText} /></PageWrapper>} />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  return (
    <CartProvider>
      <Router>
        <div className="App">
          <Navbar />
          {/* TADY BYLA TA CHYBA - DOPLNĚNO VOLÁNÍ LIŠTY */}
          <CookieConsent /> 
          <AnimatedRoutes />
          <Footer />
        </div>
      </Router>
    </CartProvider>
  );
}

export default App;